import React, { useEffect, useState } from 'react';
import { KeyIcon, ShieldCheckIcon, ClockIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import {
  fetchPermissionsForAccount,
  type PermissionState,
  type PermissionAuditLog
} from '../../services/MasterAdmin/adminPermissionService';

interface PermissionTableProps {
  type: 'user' | 'vendor';
  id: string | undefined;
}

const formatDateTime = (dateString: string | null | undefined) => {
  if (dateString == null || dateString === '') return '—';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getStatusBadge = (status: string) => {
  const normalized = String(status || '').toUpperCase();
  switch (normalized) {
    case 'GRANTED':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 ring-1 ring-emerald-500/20">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Granted
        </span>
      );
    case 'DENIED':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-300 ring-1 ring-rose-500/20">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
          Denied
        </span>
      );
    case 'PERMANENTLY_DENIED':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 ring-1 ring-red-500/20">
          <span className="w-1.5 h-1.5 rounded-full bg-red-600" />
          Permanently Denied
        </span>
      );
    case 'RESTRICTED':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 ring-1 ring-amber-500/20">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
          Restricted
        </span>
      );
    case 'PROMPT':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 ring-1 ring-blue-500/20">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
          Prompt
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
          {status}
        </span>
      );
  }
};

const getEventBadge = (event: string) => {
  const normalized = String(event || '').toUpperCase();
  switch (normalized) {
    case 'GRANTED':
      return (
        <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400">
          GRANTED
        </span>
      );
    case 'REVOKED':
      return (
        <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-semibold bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400">
          REVOKED
        </span>
      );
    case 'DENIED':
      return (
        <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-semibold bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-400">
          DENIED
        </span>
      );
    default:
      return (
        <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
          {event}
        </span>
      );
  }
};

const getPlatformBadge = (platform: string) => {
  const normalized = String(platform || '').toUpperCase();
  switch (normalized) {
    case 'FLUTTER_USER_APP':
      return 'Flutter User App';
    case 'FLUTTER_PLUS_APP':
      return 'Flutter Plus App';
    case 'WEB_BROWSER':
      return 'Web Browser';
    default:
      return platform;
  }
};

const PermissionTable: React.FC<PermissionTableProps> = ({ type, id }) => {
  const [states, setStates] = useState<PermissionState[]>([]);
  const [auditLogs, setAuditLogs] = useState<PermissionAuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let active = true;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchPermissionsForAccount(type, id);
        if (active && response.success) {
          setStates(response.data.states || []);
          setAuditLogs(response.data.auditLogs || []);
        } else if (active) {
          setError('Failed to load permissions');
        }
      } catch (err) {
        if (active) {
          console.error('Error fetching permissions:', err);
          setError('Failed to load permissions');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadData();

    return () => {
      active = false;
    };
  }, [type, id]);

  if (!id) return null;

  return (
    <div className="space-y-6">
      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div className="p-4 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 text-sm text-rose-600 dark:text-rose-300">
          {error}
        </div>
      )}

      {/* Main Content */}
      {!loading && !error && (
        <>
          {/* Permission States Card */}
          <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
            <div className="flex items-center gap-2 mb-5">
              <KeyIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Active App & Browser Permissions</h2>
            </div>

            {states.length === 0 ? (
              <div className="py-8 text-center text-gray-500 dark:text-gray-400 flex flex-col items-center justify-center">
                <ShieldExclamationIcon className="w-12 h-12 text-gray-300 mb-2" />
                <p className="text-sm">No permissions have been synced or tracked for this account yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40">
                <table className="min-w-full w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-800/60 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400 font-medium">Permission</th>
                      <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400 font-medium">Platform</th>
                      <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400 font-medium">Status</th>
                      <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400 font-medium">Last Checked</th>
                      <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400 font-medium">Granted At</th>
                      <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400 font-medium">Revoked At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {states.map((s) => (
                      <tr key={s.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors">
                        <td className="px-4 py-3 text-gray-900 dark:text-white font-medium capitalize">
                          {s.permissionName}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                          {getPlatformBadge(s.clientType)}
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(s.status)}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {formatDateTime(s.lastCheckedAt)}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {formatDateTime(s.grantedAt)}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {formatDateTime(s.revokedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Audit Logs Card */}
          <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
            <div className="flex items-center gap-2 mb-5">
              <ClockIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Permission Audit Logs (History)</h2>
            </div>

            {auditLogs.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No event logs recorded.</p>
            ) : (
              <div className="max-h-[30rem] overflow-x-auto overflow-y-auto rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 scrollbar-light">
                <table className="min-w-full w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-800/60 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400 font-medium">Timestamp</th>
                      <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400 font-medium">Permission</th>
                      <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400 font-medium">Platform</th>
                      <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400 font-medium">Event</th>
                      <th className="px-4 py-3 text-left text-gray-500 dark:text-gray-400 font-medium">Device Info</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {auditLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors">
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap font-medium">
                          {formatDateTime(log.timestamp)}
                        </td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white font-medium capitalize">
                          {log.permissionName}
                        </td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                          {getPlatformBadge(log.clientType)}
                        </td>
                        <td className="px-4 py-3">
                          {getEventBadge(log.eventType)}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-[20rem] truncate" title={log.deviceInfo ? JSON.stringify(log.deviceInfo) : ''}>
                          {log.deviceInfo ? (
                            <span>
                              {log.deviceInfo.brand || ''} {log.deviceInfo.model || ''} {log.deviceInfo.osVersion ? `(OS: ${log.deviceInfo.osVersion})` : ''} {log.deviceInfo.appVersion ? `[v${log.deviceInfo.appVersion}]` : ''}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PermissionTable;
